/**
 * Backend Health Banner - Shows API status and troubleshooting
 */

import React, { useState, useEffect } from 'react';
import { backendHealthService, HealthStatus } from '../services/BackendHealthService';
import { API_BASE_URL } from '../lib/apiBase';

export const BackendHealthBanner: React.FC = () => {
  const [healthStatus, setHealthStatus] = useState<HealthStatus>({
    isOnline: true,
    lastChecked: new Date()
  });
  const [showTroubleshoot, setShowTroubleshoot] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryMessage, setRetryMessage] = useState<string | null>(null);

  useEffect(() => {
    // Subscribe to health status changes
    const unsubscribe = backendHealthService.subscribe(setHealthStatus);
    
    // Start health monitoring
    backendHealthService.start();
    
    return () => {
      unsubscribe();
      backendHealthService.stop();
    };
  }, []);

  // Don't show banner if API is online
  if (healthStatus.isOnline) {
    return null;
  }

  const runRetry = async () => {
    setIsRetrying(true);
    setRetryMessage(null);
    const status = await backendHealthService.checkHealth();
    setIsRetrying(false);
    setRetryMessage(
      status.isOnline
        ? 'Connection restored.'
        : `Still offline${status.error ? `: ${status.error}` : '.'}`
    );
  };

  return (
    <>
      <div className="bg-amber-100 text-amber-900 px-4 py-3 shadow-sm border-b border-amber-300">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div>
              <p className="font-medium">API Offline</p>
              <p className="text-sm text-amber-800">
                Backend services are unavailable. Actions are disabled until connection is restored.
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={runRetry}
              disabled={isRetrying}
              className="bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-60 px-3 py-1 rounded text-sm font-medium transition-colors"
            >
              {isRetrying ? 'Retrying…' : 'Retry Connection'}
            </button>
            <button
              onClick={() => setShowTroubleshoot(true)}
              className="bg-white text-amber-800 border border-amber-300 hover:bg-amber-50 px-3 py-1 rounded text-sm font-medium transition-colors"
            >
              Troubleshoot
            </button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto mt-2 text-xs text-amber-800 flex flex-wrap gap-x-4 gap-y-1">
          <span>Last checked: {healthStatus.lastChecked.toLocaleTimeString()}</span>
          {typeof healthStatus.responseTime === 'number' && (
            <span>Response: {healthStatus.responseTime}ms</span>
          )}
          {typeof healthStatus.consecutiveFailures === 'number' && (
            <span>Failures: {healthStatus.consecutiveFailures}</span>
          )}
          {retryMessage && <span className="font-medium text-amber-900">{retryMessage}</span>}
        </div>
      </div>
      
      {showTroubleshoot && (
        <TroubleshootModal 
          healthStatus={healthStatus}
          onClose={() => setShowTroubleshoot(false)} 
        />
      )}
    </>
  );
};

interface TroubleshootModalProps {
  healthStatus: HealthStatus;
  onClose: () => void;
}

const TroubleshootModal: React.FC<TroubleshootModalProps> = ({ healthStatus, onClose }) => {
  const [isTesting, setIsTesting] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const healthUrl = `${API_BASE_URL}/api/v1/health`;
  const diagnostics = [
    `API URL: ${API_BASE_URL}`,
    `Health URL: ${healthUrl}`,
    `Last Checked: ${healthStatus.lastChecked.toISOString()}`,
    `Error: ${healthStatus.error || 'None'}`,
    `Response Time (ms): ${healthStatus.responseTime ?? 'N/A'}`,
    `Consecutive Failures: ${healthStatus.consecutiveFailures ?? 0}`,
    `User Agent: ${navigator.userAgent}`,
    `Path: ${window.location.pathname}`,
  ].join('\n');

  const testConnection = async () => {
    setIsTesting(true);
    setStatusMessage(null);
    const status = await backendHealthService.checkHealth();
    setIsTesting(false);
    setStatusMessage(
      status.isOnline
        ? 'Connection test passed. Backend is online.'
        : `Connection test failed${status.error ? `: ${status.error}` : '.'}`
    );
  };

  const copyDiagnostics = async () => {
    try {
      await navigator.clipboard.writeText(diagnostics);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };
  
  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
      <div className="bg-white rounded-lg max-w-2xl w-full mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">API Connection Troubleshooting</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-2">Connection Status</h3>
            <div className="space-y-2 text-sm">
              <div>
                <span className="font-medium">API URL:</span> {API_BASE_URL}
              </div>
              <div>
                <span className="font-medium">Last Checked:</span> {healthStatus.lastChecked.toLocaleString()}
              </div>
              {healthStatus.error && (
                <div>
                  <span className="font-medium">Error:</span> 
                  <code className="ml-2 text-red-600">{healthStatus.error}</code>
                </div>
              )}
            </div>
          </div>

          <div>
            <h3 className="font-medium text-gray-900 mb-2">Troubleshooting Steps</h3>
            <ol className="list-decimal list-inside space-y-2 text-sm text-gray-600">
              <li>Verify the backend server is running</li>
              <li>Check that the API URL in your environment variables is correct</li>
              <li>Ensure there are no firewall or network restrictions</li>
              <li>Try refreshing the page</li>
              <li>Contact support if the issue persists</li>
            </ol>
          </div>

          <div>
            <h3 className="font-medium text-gray-900 mb-2">Development Mode</h3>
            <p className="text-sm text-gray-600 mb-3">
              If you're running in development mode, start the backend server:
            </p>
            <div className="bg-gray-900 text-green-400 p-3 rounded font-mono text-sm">
              cd backend<br />
              python -m uvicorn app.main:app --host 0.0.0.0 --port 8000 --reload
            </div>
          </div>

          <div>
            <h3 className="font-medium text-gray-900 mb-2">Quick Actions</h3>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => window.open(healthUrl, '_blank', 'noopener,noreferrer')}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded text-sm font-medium transition-colors"
              >
                Open Health Endpoint
              </button>
              <button
                onClick={copyDiagnostics}
                className="bg-slate-700 hover:bg-slate-800 text-white px-3 py-2 rounded text-sm font-medium transition-colors"
              >
                {copied ? 'Copied' : 'Copy Diagnostics'}
              </button>
              <button
                onClick={() => window.location.reload()}
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-3 py-2 rounded text-sm font-medium transition-colors"
              >
                Reload App
              </button>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <button
            onClick={testConnection}
            disabled={isTesting}
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white px-4 py-2 rounded font-medium transition-colors"
          >
            {isTesting ? 'Testing…' : 'Test Connection'}
          </button>
          <button
            onClick={onClose}
            className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded font-medium transition-colors"
          >
            Close
          </button>
        </div>
        {statusMessage && (
          <p className={`mt-3 text-sm ${statusMessage.includes('passed') ? 'text-emerald-700' : 'text-red-700'}`}>
            {statusMessage}
          </p>
        )}
      </div>
    </div>
  );
};
